import ConfirmModal from "@/src/components/action-modals/confirm-modal";
import { BlocksApiArticle } from "@/src/models/wiki.models";
import {
  useWikiDeleteArticle,
  useWikiDraftArticles,
} from "@/src/services/wiki/wiki.hooks";
import { router } from "expo-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  FlatList,
  Image,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import WikiArticleCard from "../wiki-article-card/wiki-article-card";
import WikiCardSkeleton from "../wiki-card-skeleton";

const BREAKPOINT_LG = 1024;

const navigateToEdit = (article: BlocksApiArticle) => {
  router.push({
    pathname: "/(tabs)/wiki/wiki-creation",
    params: {
      draftId: article.ItemId,
      Title: article.Title,
      Content: article.Content,
      categoryId: article.categoryIds ?? "",
      coverImageUrl: article.coverImageUrl ?? "",
      attachments: JSON.stringify(
        (article.attachmentUrls ?? []).map((att) => ({
          id: att.id,
          name: att.name,
          size: att.size,
          url: att.url,
          mimeType: att.mimeType,
        })),
      ),
    },
  });
};

type WikiDraftCardProps = Readonly<{
  article: BlocksApiArticle;
  onDelete: (id: string) => void;
  itemWidthClass: string;
}>;

function WikiDraftCard({
  article,
  onDelete,
  itemWidthClass,
}: WikiDraftCardProps) {
  const { t } = useTranslation();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const dropdownItems = [
    { label: t("BTN_UPDATE"), onPress: () => navigateToEdit(article) },
    {
      label: t("BTN_DELETE"),
      onPress: () => setShowDeleteConfirm(true),
      destructive: true,
    },
  ];

  return (
    <>
      <ConfirmModal
        visible={showDeleteConfirm}
        title="BTN_DELETE"
        description="LABEL_DESC_DELETE_WIKI"
        confirmText={t("BTN_DELETE")}
        cancelText={t("BTN_CANCEL")}
        onConfirm={() => {
          onDelete(article.ItemId);
          setShowDeleteConfirm(false);
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
      <WikiArticleCard
        article={article}
        itemWidthClass={itemWidthClass}
        dropdownItems={dropdownItems}
        onPress={() => navigateToEdit(article)}
        emptyTitleLabel={t("LABEL_NO_TITLE")}
      />
    </>
  );
}

export default function WikiDraft({
  searchText = "",
}: {
  searchText?: string;
}) {
  const { t } = useTranslation();
  const { width } = useWindowDimensions();

  // Small screens show 2 cards per row (never 1); large → 3.
  const numColumns = width >= BREAKPOINT_LG ? 3 : width >= 768 ? 2 : 1;
  const itemWidthClass =
    numColumns === 3 ? "w-[32.4%]" : numColumns === 2 ? "w-[49%]" : "w-full";
  const columnWrapperStyle = numColumns > 1 ? { gap: 10 } : undefined;
  const contentContainerStyle = { gap: 12 };

  const { data: allDrafts = [], isLoading } = useWikiDraftArticles(searchText);
  const { mutate: deleteArticle } = useWikiDeleteArticle();
  const [localDeletedIds, setLocalDeletedIds] = useState<string[]>([]);

  const handleDelete = (id: string) => {
    setLocalDeletedIds((prev) => [...prev, id]);
    // Pass the article so its attachments are deleted from storage too.
    deleteArticle({
      itemId: id,
      entity: allDrafts.find((article) => article.ItemId === id),
    });
  };

  const drafts = allDrafts.filter(
    (article) => !localDeletedIds.includes(article.ItemId),
  );

  if (isLoading) {
    return (
      <View className="flex-1">
        <View className="flex-row flex-wrap" style={{ gap: 10 }}>
          {Array.from({ length: numColumns * 2 }).map((_, index) => (
            <WikiCardSkeleton
              key={index}
              className={itemWidthClass}
            />
          ))}
        </View>
      </View>
    );
  }

  if (!drafts.length) {
    return (
      <View className="flex-1 items-center justify-center gap-4">
        <Image
          source={require("@/src/assets/images/empty-state.png")}
          style={{ width: 100, height: 100 }}
          resizeMode="contain"
        />
        <Text className="text-ash-1 text-sm">
          {searchText.trim() ? t("HINT_WIKI_FILTER") : t("LABEL_NO_ARTICLES")}
        </Text>
      </View>
    );
  }

  return (
    <View className="flex-1">
      <FlatList
        key={numColumns}
        data={drafts}
        keyExtractor={(item) => item.ItemId}
        numColumns={numColumns}
        columnWrapperStyle={columnWrapperStyle}
        contentContainerStyle={contentContainerStyle}
        showsVerticalScrollIndicator={false}
        renderItem={({ item }) => (
          <WikiDraftCard
            article={item}
            onDelete={handleDelete}
            itemWidthClass={itemWidthClass}
          />
        )}
      />
    </View>
  );
}
